import './settings/settings-leaf.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { templateApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, EmptyState, LoadingBlock, Select, TextArea, TextInput } from './ui.tsx';
import { MoneyInput } from './money-input.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { useLocalizedContent } from '../i18n/localized.ts';
import LocalizedFields, {
  emptyTranslationDraft,
  translationDraftFrom,
  translationPayload,
  type TranslationDraft,
} from './localized-fields.tsx';
import type { SupportedLanguage } from '@shared/constants/languages.ts';
import { emptyTemplateTags, type TemplateTags } from '@shared/constants/template-taxonomy.ts';
import { TaxonomyFields } from './taxonomy-fields.tsx';
import { useListMotion } from '../hooks/list-motion.ts';
import type { RoomTemplate, ServiceTemplate } from '@shared/types/index.ts';

/**
 * Default room types and what each one starts with (spec §10.9).
 *
 * A new company is seeded with a starter library, and every quote is built by
 * pressing "+ Kitchen" and friends on the rooms step — so these defaults decide
 * what an estimator sees, which tasks are ticked before anyone touches them,
 * and how long each one is assumed to take. Reading needs `templates:read`;
 * changing anything needs `templates:write`, which an Estimator does not have.
 *
 * Editing a room type never rewrites a quote that already exists: a quote
 * copies these values in when the room is added and keeps its own from then on
 * (spec §8.11).
 */

/** Sentinel for "the add form is open", which has no id yet. */
const NEW_ROOM = '__new__';

/** The field paths this form has a control for. See `reportError`. */
const RENDERED_FIELDS = new Set(['name', 'description', 'defaultFlatPriceCents', 'services']);

/**
 * Where a newly created room type goes: after the last one.
 *
 * Counting the list is not the same thing. Orders go non-contiguous as soon as
 * a room type is hidden — 0, 2, 3 has a count of 3, which ties the existing
 * last room and lets the API's secondary sort by name decide the order.
 */
function nextDisplayOrder(items: RoomTemplate[] | undefined): number {
  if (!items || items.length === 0) return 0;
  return Math.max(...items.map((item) => item.displayOrder)) + 1;
}

interface DraftService {
  serviceTemplateId: string;
  defaultSelected: boolean;
  /** Raw input. Empty means "use the task's own default minutes". */
  minutesOverride: string;
  /** Raw input. Empty means this task has no fixture or area default in the room. */
  measuredQuantity: string;
}

interface Draft {
  name: string;
  description: string;
  categoryCode: string;
  tags: TemplateTags;
  isFavorite: boolean;
  /**
   * The two above in the company's OTHER languages (see localized-text.ts). The base fields
   * stay the company's own wording — what a customer's quote prints — and
   * these are what staff reading the app in another language see.
   */
  nameTranslations: TranslationDraft;
  descriptionTranslations: TranslationDraft;
  isActive: boolean;
  /** `null` prices the room type by its tasks, as every room type did. */
  flatPriceCents: number | null;
  services: DraftService[];
}

function emptyDraft(): Draft {
  return {
    name: '',
    description: '',
    categoryCode: '',
    tags: emptyTemplateTags(),
    isFavorite: false,
    nameTranslations: emptyTranslationDraft(),
    descriptionTranslations: emptyTranslationDraft(),
    isActive: true,
    flatPriceCents: null,
    services: [],
  };
}

function draftFrom(template: RoomTemplate): Draft {
  return {
    name: template.name,
    description: template.description ?? '',
    categoryCode: template.categoryCode ?? '',
    tags: template.tags ?? emptyTemplateTags(),
    isFavorite: template.isFavorite ?? false,
    nameTranslations: translationDraftFrom(template.nameTranslations),
    descriptionTranslations: translationDraftFrom(template.descriptionTranslations),
    isActive: template.isActive,
    flatPriceCents: template.defaultFlatPriceCents ?? null,
    services: [...template.services]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((service) => ({
        serviceTemplateId: service.serviceTemplateId,
        defaultSelected: service.defaultSelected,
        minutesOverride: service.defaultMinutesOverride === null ? '' : String(service.defaultMinutesOverride),
        measuredQuantity: service.defaultMeasuredQuantity == null ? '' : String(service.defaultMeasuredQuantity),
      })),
  };
}

/**
 * Blank means "no override" — the task's own default minutes are used — which
 * is a different answer from zero, so it cannot be coerced to 0 here.
 */
function parseOverride(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const minutes = Number(trimmed);
  if (!Number.isFinite(minutes)) return null;
  return Math.max(0, Math.round(minutes));
}

/** Blank is "no measured default", which is different from zero fixtures. */
function parseMeasured(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const quantity = Number(trimmed);
  if (!Number.isFinite(quantity)) return null;
  return Math.max(0, Math.round(quantity));
}

function draftPayload(draft: Draft, baseLanguage: SupportedLanguage): Record<string, unknown> {
  const name = draft.name.trim();
  const description = draft.description.trim();
  return {
    name,
    description,
    // The base language's entry comes from the fields above, not from the
    // form's own boxes — see `translationPayload` for why they must agree.
    nameTranslations: translationPayload(draft.nameTranslations, baseLanguage, name),
    descriptionTranslations: translationPayload(draft.descriptionTranslations, baseLanguage, description),
    isActive: draft.isActive,
    categoryCode: draft.categoryCode || null,
    tags:
      draft.tags.segments.length + draft.tags.jobTypes.length + draft.tags.floorTypes.length === 0 ? null : draft.tags,
    isFavorite: draft.isFavorite,
    defaultFlatPriceCents: draft.flatPriceCents,
    // Position in the list *is* the display order, so the arrows in the editor
    // are the only thing that has to think about ordering.
    services: draft.services.map((service, index) => ({
      serviceTemplateId: service.serviceTemplateId,
      defaultSelected: service.defaultSelected,
      defaultMinutesOverride: parseOverride(service.minutesOverride),
      defaultMeasuredQuantity: parseMeasured(service.measuredQuantity),
      displayOrder: index,
    })),
  };
}

export default function RoomTemplatesSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can, company } = useSession();
  // The room type's price is a company-wide default, so it is shown in the
  // company's currency rather than any one quote's snapshot.
  const currencyCode = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const localized = useLocalizedContent();

  const [showHidden, setShowHidden] = useState(false);
  // The arrows move a row between one render and the next, in a list long
  // enough that the row moved past may be off screen. See `useListMotion`.
  const motion = useListMotion();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /*
    Always the whole library, hidden room types included; the toggle only
    decides what is drawn. Reordering needs it: the arrows act on the visible
    rows, but display order is a single sequence that hidden rooms sit inside,
    and renumbering only what is on screen would park an active room on top of
    a hidden one's position. It also makes the toggle instant.
  */
  const rooms = useQuery({
    queryKey: ['room-templates', 'with-hidden'],
    queryFn: () => templateApi.rooms(true),
  });

  /*
    Deliberately the full task library, hidden ones included. A room type can
    still link a task that was hidden after the fact, and looking those up in
    the active-only list would render a nameless row the owner cannot identify
    — or remove. They are labeled as hidden instead; only active tasks are
    offered when adding.
  */
  const services = useQuery({
    queryKey: ['service-templates', true],
    queryFn: () => templateApi.services(true),
  });

  const writable = can('templates:write');
  const serviceById = new Map((services.data?.items ?? []).map((service) => [service.id, service]));

  const all = rooms.data?.items ?? [];
  const items = showHidden ? all : all.filter((template) => template.isActive);

  function refresh() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['room-templates'] }),
      queryClient.invalidateQueries({ queryKey: ['service-templates'] }),
    ]);
  }

  /*
    Field errors are only silent-safe when the form can actually show them.
    The server names a bad task by its path — `services.0.defaultMinutesOverride`
    — and this form has no control bound to that, so suppressing the general
    alert on the strength of "there were field errors" left Save doing nothing
    visible. Anything unrecognized falls back to the alert.
  */
  function reportError(caught: unknown, fallback: string) {
    if (!(caught instanceof ApiRequestError)) {
      setFieldErrors({});
      setError(fallback);
      return;
    }

    setFieldErrors(caught.fields);
    const paths = Object.keys(caught.fields);
    const shown = paths.filter((path) => RENDERED_FIELDS.has(path));
    setError(paths.length > 0 && shown.length === paths.length ? null : caught.message);
  }

  /*
    Which language a row's own `name` and `description` are written in comes
    from the ROW, not from the company: `company.language` is what the company
    SENDS in and the owner changes it in Settings, while this row was written
    once and stays written in whatever it was written in. Reading the mutable
    setting would file an English name under `es` on the next save and take the
    real Spanish with it. A room type being created has no row yet, so it
    records the language it is being typed in.
  */
  const editingRow = all.find((template) => template.id === editing) ?? null;
  const baseLanguage = (editing === NEW_ROOM ? null : editingRow)?.textLanguage ?? localized.language;

  const save = useMutation({
    mutationFn: () =>
      editing === NEW_ROOM
        ? templateApi.createRoom({
            ...draftPayload(draft, baseLanguage),
            textLanguage: baseLanguage,
            displayOrder: nextDisplayOrder(all),
          })
        : templateApi.updateRoom(editing ?? '', draftPayload(draft, baseLanguage)),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setDraft(emptyDraft());
      setError(null);
      setFieldErrors({});
    },
    onError: (caught) => reportError(caught, t('roomTemplates.saveError')),
  });

  const hide = useMutation({
    mutationFn: (id: string) => templateApi.deactivateRoom(id),
    onSuccess: () => refresh(),
    onError: (caught) => reportError(caught, t('roomTemplates.saveError')),
  });

  const restore = useMutation({
    mutationFn: (id: string) => templateApi.updateRoom(id, { isActive: true }),
    onSuccess: () => refresh(),
    onError: (caught) => reportError(caught, t('roomTemplates.saveError')),
  });

  /*
    A move is one request carrying the company's whole ordering, which the
    server applies in a single transaction.

    Both halves of that matter. Per-row PATCHes could fail partway and leave
    the library half-renumbered — orders duplicated, in an order nobody chose,
    and not visibly wrong until the next reload. And the sequence has to
    include the hidden rows even though the arrows cannot see them: a move is
    expressed as "put this row where that visible neighbor is", and everything
    else keeps its relative place.
  */
  const reorder = useMutation({
    mutationFn: (input: { index: number; delta: number }) => {
      const moved = items[input.index];
      const neighbor = items[input.index + input.delta];
      if (!moved || !neighbor) return Promise.resolve(null);

      const next = all.filter((room) => room.id !== moved.id);
      const at = next.findIndex((room) => room.id === neighbor.id);
      if (at === -1) return Promise.resolve(null);
      next.splice(input.delta > 0 ? at + 1 : at, 0, moved);

      return templateApi.reorderRooms(next.map((room) => room.id));
    },
    onSuccess: () => refresh(),
    // Refreshed on failure too: a rejected reorder leaves the cached list
    // showing an order the server never accepted.
    onError: (caught) => {
      void refresh();
      reportError(caught, t('roomTemplates.saveError'));
    },
  });

  function startAdd() {
    setEditing(NEW_ROOM);
    setDraft(emptyDraft());
    setError(null);
    setFieldErrors({});
  }

  function startEdit(template: RoomTemplate) {
    setEditing(template.id);
    setDraft(draftFrom(template));
    setError(null);
    setFieldErrors({});
  }

  function cancel() {
    setEditing(null);
    setDraft(emptyDraft());
    setError(null);
    setFieldErrors({});
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  const busy = save.isPending || hide.isPending || restore.isPending || reorder.isPending;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="room-types-heading">
      <div className="work-section-heading">
        <h2 id="room-types-heading" className="text-lg font-semibold">
          {t('roomTemplates.heading')}
        </h2>
        {/*
          Both forms are gated on the task library having arrived. Without it
          the editor renders every existing link as "Unknown task" over live
          reorder and remove buttons — an invitation to delete the wrong one —
          and the add form offers a picker with nothing in it.
        */}
        {writable && editing === null ? (
          <Button onClick={startAdd} disabled={!services.isSuccess}>
            {t('roomTemplates.addRoomType')}
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('roomTemplates.description')}</p>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {services.isError ? (
        <div className="mb-4">
          <Alert tone="error">{t('roomTemplates.taskLibraryError')}</Alert>
        </div>
      ) : null}

      <Checkbox
        label={t('roomTemplates.showHidden')}
        checked={showHidden}
        onChange={(event) => {
          const next = event.target.checked;
          setShowHidden(next);
          /*
            Only throw the draft away when the row holding it is about to leave
            the list — turning the option off while editing a hidden room type.
            Everything else stays on screen either way, and dropping an edit
            someone was halfway through for a filter change is not a trade the
            filter is worth.
          */
          const editedLeaves = !next && all.some((item) => item.id === editing && !item.isActive);
          if (editedLeaves) cancel();
        }}
      />

      {editing === NEW_ROOM ? (
        <RoomTemplateForm
          draft={draft}
          setDraft={setDraft}
          baseLanguage={baseLanguage}
          services={services.data?.items ?? []}
          serviceById={serviceById}
          fieldErrors={fieldErrors}
          busy={save.isPending}
          submitLabel={t('roomTemplates.createRoomType')}
          onSubmit={handleSubmit}
          onCancel={cancel}
        />
      ) : null}

      {rooms.isLoading ? <LoadingBlock label={t('roomTemplates.loading')} /> : null}

      {!rooms.isLoading && items.length === 0 && editing !== NEW_ROOM ? (
        <EmptyState title={t('roomTemplates.emptyTitle')}>
          <p>{t('roomTemplates.emptyMessage')}</p>
        </EmptyState>
      ) : null}

      <ul className="divide-y divide-[color:var(--color-line)]">
        {items.map((template, index) => {
          const defaultOn = template.services.filter((service) => service.defaultSelected).length;
          const description = localized.optionalText(template.description, template.descriptionTranslations);

          if (editing === template.id) {
            return (
              <li key={template.id} className="py-3">
                <RoomTemplateForm
                  draft={draft}
                  setDraft={setDraft}
                  baseLanguage={baseLanguage}
                  services={services.data?.items ?? []}
                  serviceById={serviceById}
                  fieldErrors={fieldErrors}
                  busy={save.isPending}
                  submitLabel={t('roomTemplates.saveRoomType')}
                  onSubmit={handleSubmit}
                  onCancel={cancel}
                />
              </li>
            );
          }

          return (
            <li
              key={template.id}
              ref={motion.register(template.id)}
              className="flex flex-wrap items-start justify-between gap-3 py-3"
            >
              <div className="min-w-56 flex-1">
                <p className="font-medium">
                  {/* Read in the viewer's language (see localized-text.ts); the form labels
                      which language each field is. */}
                  {localized.text(template.name, template.nameTranslations)}
                  {!template.isActive ? (
                    <span className="ml-2 badge border-[color:var(--color-muted)] text-[color:var(--color-muted)]">
                      {t('roomTemplates.hidden')}
                    </span>
                  ) : null}
                </p>
                {/*
                  Resolved BEFORE the decision to draw it. A room type
                  translated into the reader's language but left blank in the
                  company's own — which the form and the schema both allow —
                  has a description to show and no base text to gate it on.
                */}
                {description ? <p className="text-sm text-[color:var(--color-muted)]">{description}</p> : null}
                <p className="text-sm text-[color:var(--color-muted)]">
                  {template.defaultFlatPriceCents === null
                    ? t('roomTemplates.pricedByTime')
                    : t('roomTemplates.pricedAt', {
                        price: formatCents(template.defaultFlatPriceCents, currencyCode, getDateLocale()),
                      })}
                  {' · '}
                  {t('roomTemplates.taskSummary', { defaultOn, total: template.services.length })}
                </p>
              </div>

              {writable ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={editing !== null || busy || !services.isSuccess}
                    onClick={() => startEdit(template)}
                  >
                    {t('roomTemplates.edit')}
                  </Button>
                  <Button
                    disabled={index === 0 || editing !== null || busy}
                    aria-label={t('roomTemplates.moveUp', {
                      room: localized.text(template.name, template.nameTranslations),
                    })}
                    onClick={() => reorder.mutate({ index, delta: -1 })}
                  >
                    ↑
                  </Button>
                  <Button
                    disabled={index === items.length - 1 || editing !== null || busy}
                    aria-label={t('roomTemplates.moveDown', {
                      room: localized.text(template.name, template.nameTranslations),
                    })}
                    onClick={() => reorder.mutate({ index, delta: 1 })}
                  >
                    ↓
                  </Button>
                  {template.isActive ? (
                    <Button
                      variant="danger"
                      disabled={editing !== null || busy}
                      onClick={() => hide.mutate(template.id)}
                    >
                      {t('roomTemplates.hide')}
                    </Button>
                  ) : (
                    <Button disabled={editing !== null || busy} onClick={() => restore.mutate(template.id)}>
                      {t('roomTemplates.restore')}
                    </Button>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {writable ? <p className="field-hint mt-4">{t('roomTemplates.hideNote')}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The add/edit form
// ---------------------------------------------------------------------------

interface RoomTemplateFormProps {
  draft: Draft;
  setDraft: (update: (current: Draft) => Draft) => void;
  /** The language the base name and description are written in. */
  baseLanguage: SupportedLanguage;
  services: ServiceTemplate[];
  serviceById: Map<string, ServiceTemplate>;
  fieldErrors: Record<string, string>;
  busy: boolean;
  submitLabel: string;
  onSubmit: (event: FormEvent) => void;
  onCancel: () => void;
}

function RoomTemplateForm({
  draft,
  setDraft,
  baseLanguage,
  services,
  serviceById,
  fieldErrors,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: RoomTemplateFormProps) {
  const { t } = useTranslation();
  const { company } = useSession();
  /* Task names as this reader reads them; see the section above. */
  const localized = useLocalizedContent();
  // A room type's price is a company-wide default, so it is entered in the
  // company's currency rather than any one quote's snapshot.
  const currencyCode = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const [toAdd, setToAdd] = useState('');
  // ↑, ↓ and Remove all rearrange a list that is several rows tall by the time
  // it is worth reordering, and all three are otherwise silent about what they
  // did — a removed row's neighbors close over it between one frame and the
  // next. See `useListMotion`.
  const motion = useListMotion();

  const chosen = new Set(draft.services.map((service) => service.serviceTemplateId));
  // Only active tasks are on offer: adding a hidden one would put a task on
  // every new quote that the library no longer lists.
  const available = services.filter((service) => service.isActive && !chosen.has(service.id));

  function patchService(index: number, patch: Partial<DraftService>) {
    setDraft((current) => ({
      ...current,
      services: current.services.map((service, i) => (i === index ? { ...service, ...patch } : service)),
    }));
  }

  function moveService(index: number, delta: number) {
    setDraft((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.services.length) return current;
      const next = [...current.services];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return { ...current, services: next };
    });
  }

  /**
   * Drops a task from the draft, straight away.
   *
   * Deliberately not played out first, the way a room is on the quote itself.
   * The draft here is what Save submits, and holding the change back for the
   * length of a fade puts a window — small, but real — in which Save sends a
   * room type still carrying a task the estimator has watched disappear. The
   * rows below still slide up into the gap, which is most of what the movement
   * was saying; what is lost is the departing row fading rather than going, and
   * that is not worth a form that can submit something other than what is on
   * screen.
   */
  function removeService(index: number) {
    setDraft((current) => ({ ...current, services: current.services.filter((_, i) => i !== index) }));
  }

  function addService() {
    if (!toAdd) return;
    setDraft((current) =>
      current.services.some((service) => service.serviceTemplateId === toAdd)
        ? current
        : {
            ...current,
            services: [
              ...current.services,
              { serviceTemplateId: toAdd, defaultSelected: true, minutesOverride: '', measuredQuantity: '' },
            ],
          },
    );
    setToAdd('');
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mb-6 rounded-md border border-[color:var(--color-line)] p-4">
      {/*
        Everything the save is carrying goes read-only while it is in flight.
        The mutation closed over the draft as it was at submit time, so edits
        made during a slow save stay on screen, look accepted, and then vanish
        when the response closes the form. Cancel stays outside the fieldset:
        a save that hangs must not trap the user in it.
      */}
      <fieldset disabled={busy} className="contents">
        <TextInput
          label={t('roomTemplates.nameLabel')}
          required
          value={draft.name}
          error={fieldErrors.name}
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
        />
        <TextArea
          label={t('roomTemplates.descriptionLabel')}
          rows={2}
          hint={t('roomTemplates.descriptionHint')}
          value={draft.description}
          error={fieldErrors.description}
          onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
        />

        <LocalizedFields
          baseLanguage={baseLanguage}
          fields={[
            {
              key: 'name',
              label: t('roomTemplates.nameLabel'),
              valueFor: (language) => draft.nameTranslations[language],
              onChange: (language, value) =>
                setDraft((current) => ({
                  ...current,
                  nameTranslations: { ...current.nameTranslations, [language]: value },
                })),
            },
            {
              key: 'description',
              label: t('roomTemplates.descriptionLabel'),
              multiline: true,
              valueFor: (language) => draft.descriptionTranslations[language],
              onChange: (language, value) =>
                setDraft((current) => ({
                  ...current,
                  descriptionTranslations: { ...current.descriptionTranslations, [language]: value },
                })),
            },
          ]}
        />
        {/*
          Empty is not zero. Clearing the box prices the room type from its
          tasks the way it always was; typing 0 quotes the room for nothing.
          `MoneyInput` gives back null for the first and 0 for the second.
        */}
        <MoneyInput
          currencyCode={currencyCode}
          label={t('roomTemplates.priceLabel')}
          hint={t('roomTemplates.priceHint')}
          valueCents={draft.flatPriceCents}
          error={fieldErrors.defaultFlatPriceCents}
          min={0}
          onChangeCents={(cents) => setDraft((current) => ({ ...current, flatPriceCents: cents }))}
        />

        <TaxonomyFields
          kind="room"
          categoryCode={draft.categoryCode}
          tags={draft.tags}
          isFavorite={draft.isFavorite}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        />
        <Checkbox
          label={t('roomTemplates.availableLabel')}
          hint={t('roomTemplates.availableHint')}
          checked={draft.isActive}
          onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
        />

        <h3 className="mt-4 mb-1 text-base font-semibold">{t('roomTemplates.tasksHeading')}</h3>
        <p className="field-hint mb-3">{t('roomTemplates.tasksHint')}</p>

        {fieldErrors.services ? (
          <p className="field-error" role="alert">
            {fieldErrors.services}
          </p>
        ) : null}

        {draft.services.length === 0 ? (
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('roomTemplates.noTasks')}</p>
        ) : (
          <ol className="mb-3 divide-y divide-[color:var(--color-line)]">
            {draft.services.map((service, index) => {
              const template = serviceById.get(service.serviceTemplateId);
              /*
                These rows only pick and order tasks — they never edit a task's
                customer-facing name — so they read in the reader's own
                language like every other list of task names in the app.
              */
              const name = template
                ? localized.text(template.name, template.nameTranslations)
                : t('roomTemplates.unknownTask');
              return (
                <li
                  key={service.serviceTemplateId}
                  ref={motion.register(service.serviceTemplateId)}
                  className="flex flex-col gap-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:py-2"
                >
                  <span className="w-full text-sm sm:min-w-40 sm:flex-1">
                    {name}
                    {template && !template.isActive ? (
                      <span className="ml-2 badge border-[color:var(--color-muted)] text-[color:var(--color-muted)]">
                        {t('roomTemplates.hidden')}
                      </span>
                    ) : null}
                  </span>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-5 w-5 shrink-0 rounded border-[color:var(--color-line)]"
                      checked={service.defaultSelected}
                      onChange={(event) => patchService(index, { defaultSelected: event.target.checked })}
                    />
                    {t('roomTemplates.tickedByDefault')}
                  </label>

                  {/*
                  A bare number box next to a task name says nothing about what
                  the number is. The unit is spelled out beside it, and the
                  full sentence is on the input's own label for a screen reader
                  — which is why the visible "minutes" is hidden from one.
                */}
                  <div className="flex items-center gap-2">
                    <label className="sr-only" htmlFor={`override-${service.serviceTemplateId}`}>
                      {t('roomTemplates.minutesFor', { name })}
                    </label>
                    <input
                      id={`override-${service.serviceTemplateId}`}
                      type="number"
                      min={0}
                      max={1000}
                      className="field-control max-w-24 text-right"
                      // The task's own default is the placeholder, so leaving the
                      // box empty visibly means "whatever the library says".
                      placeholder={template ? String(template.defaultMinutes) : ''}
                      value={service.minutesOverride}
                      onChange={(event) => patchService(index, { minutesOverride: event.target.value })}
                    />
                    <span aria-hidden="true" className="text-sm text-[color:var(--color-muted)]">
                      {t('roomTemplates.minutesUnit')}
                    </span>
                  </div>

                  {template?.measurementBasis === 'area' || template?.measurementBasis === 'fixture' ? (
                    <div className="flex items-center gap-2">
                      <label className="sr-only" htmlFor={`measured-${service.serviceTemplateId}`}>
                        {t('roomTemplates.measuredFor', { name })}
                      </label>
                      <input
                        id={`measured-${service.serviceTemplateId}`}
                        type="number"
                        min={0}
                        className="field-control max-w-24 text-right"
                        value={service.measuredQuantity}
                        onChange={(event) => patchService(index, { measuredQuantity: event.target.value })}
                      />
                      <span aria-hidden="true" className="text-sm text-[color:var(--color-muted)]">
                        {t('roomTemplates.measuredUnit')}
                      </span>
                    </div>
                  ) : null}

                  <div className="flex gap-2">
                    <Button
                      disabled={index === 0}
                      aria-label={t('roomTemplates.moveTaskUp', { name })}
                      onClick={() => moveService(index, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      disabled={index === draft.services.length - 1}
                      aria-label={t('roomTemplates.moveTaskDown', { name })}
                      onClick={() => moveService(index, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      variant="danger"
                      aria-label={t('roomTemplates.removeTask', { name })}
                      onClick={() => removeService(index)}
                    >
                      {t('common.remove')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <Select
              label={t('roomTemplates.addTaskLabel')}
              value={toAdd}
              disabled={available.length === 0}
              onChange={(event) => setToAdd(event.target.value)}
              options={[
                { value: '', label: t('roomTemplates.chooseTask') },
                ...available.map((service) => {
                  const label = localized.text(service.name, service.nameTranslations);
                  const category = localized.optionalText(service.category, service.categoryTranslations);
                  return { value: service.id, label: category ? `${category} · ${label}` : label };
                }),
              ]}
            />
          </div>
          <div className="mb-4">
            <Button onClick={addService} disabled={!toAdd}>
              {t('roomTemplates.addTask')}
            </Button>
          </div>
        </div>
      </fieldset>

      <div className="flex gap-3">
        <Button type="submit" variant="primary" busy={busy} disabled={!draft.name.trim()}>
          {submitLabel}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}
